// src/hooks/usePagination.js
import { useState, useCallback, useEffect } from 'react';

/**
 * Custom hook for pagination management
 * @param {number} initialPage - Initial page number (default: 1)
 * @param {number} initialItemsPerPage - Initial items per page (default: 10)
 * @returns {Object} Pagination state and handlers
 */
export const usePagination = (initialPage = 1, initialItemsPerPage = 10) => {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);

  // Handle page change
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  // Handle items per page change
  const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  }, []);

  // Reset to first page
  const resetToFirstPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  // Go to next page
  const goToNextPage = useCallback((totalPages) => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  }, []);

  // Go to previous page
  const goToPreviousPage = useCallback(() => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  }, []);

  // Go to first page
  const goToFirstPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  // Go to last page
  const goToLastPage = useCallback((totalPages) => {
    setCurrentPage(totalPages);
  }, []);

  // Calculate pagination info
  const getPaginationInfo = useCallback((totalItems, totalPages) => {
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);
    
    return {
      startItem,
      endItem,
      totalItems,
      currentPage,
      totalPages,
      itemsPerPage,
      hasNextPage: currentPage < totalPages,
      hasPreviousPage: currentPage > 1,
      isFirstPage: currentPage === 1,
      isLastPage: currentPage === totalPages
    };
  }, [currentPage, itemsPerPage]);

  // Generate page numbers for pagination component
  const getPageNumbers = useCallback((totalPages, maxVisiblePages = 5) => {
    const pages = [];
    const half = Math.floor(maxVisiblePages / 2);
    
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxVisiblePages - 1);
    
    // Adjust start if we're near the end
    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    return pages;
  }, [currentPage]);

  return {
    // State
    currentPage,
    itemsPerPage,
    
    // Handlers
    handlePageChange,
    handleItemsPerPageChange,
    resetToFirstPage,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    goToLastPage,
    
    // Utilities
    getPaginationInfo,
    getPageNumbers
  };
};

/**
 * Hook for managing filters with automatic pagination reset
 * Combines both filters and pagination state
 * @param {Object} initialFilters - Initial filter state
 * @param {number} initialPageSize - Initial page size (default: 10)
 * @returns {Object} Filter and pagination state and handlers
 */
export const useFiltersWithPagination = (initialFilters = {}, initialPageSize = 10) => {
  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1); // Reset to first page when filter changes
  }, []);

  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPage(1); // Reset to first page when filters change
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(initialFilters);
    setPage(1); // Reset to first page when filters are cleared
  }, [initialFilters]);

  const goToPage = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const changePageSize = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when page size changes
  }, []);

  const resetPagination = useCallback(() => {
    setPage(1);
  }, []);

  return {
    filters,
    updateFilter,
    updateFilters,
    resetFilters,
    page,
    pageSize,
    goToPage,
    changePageSize,
    resetPagination
  };
};